/**
 * The responsive breakpoints (spec M0-F9 §3, generalising M0-F4 §7.10).
 *
 * They are **not** CSS custom properties: a media query cannot read `var(--…)`, so a token that
 * only CSS could see would be a lie. They live here as typed constants instead, the shell reads
 * them through `matchMedia`, and a unit test in `apps/web` walks every stylesheet in the app and
 * fails on any `max-width` / `min-width` that is not one of these numbers — which is what stops
 * the fourteenth hand-written `@media (max-width: 760px)` from appearing.
 *
 * monday.com itself has none of this: it serves an app interstitial on a phone user agent
 * (`discovery/ui-reference/mobile/INDEX.md`), so every value below is a **deviation**, recorded
 * in M0-F9 §10 with the user's decision (doc 01, ADR-001 §6 — no native app, mobile-friendly
 * web) as the reason.
 */
/** Upper bound, inclusive, of each band. A band is `max-width: <value>px`. */
export const breakpoints = {
    /** ≤ 767: phone. Rail and pane are off-canvas; panels go full-screen; pickers are sheets. */
    phone: 767,
    /** ≤ 1023: small tablet. The pane floats over main with a scrim; search collapses to an icon. */
    tablet: 1023,
    /** ≤ 1279: laptop. Board-header actions shed their labels — the one rule monday itself shows. */
    laptop: 1279,
};
/** The narrowest viewport the product supports (M0-F4 §7.10 (a)). */
export const MIN_SUPPORTED_WIDTH = 360;
/**
 * The minimum tap target on a coarse pointer, in CSS pixels. 44 is Apple's HIG figure and the
 * one WCAG 2.5.5 (AAA) uses; WCAG 2.5.8 (AA) allows 24 with spacing, which we do not rely on.
 */
export const MIN_TOUCH_TARGET = 44;
/** Height of the phone bottom navigation bar (M0-F9 §4.3). */
export const BOTTOM_NAV_HEIGHT = 56;
/** `(max-width: 767px)` etc. — the exact string the shell hands to `matchMedia`. */
export const mediaQuery = (name) => `(max-width: ${breakpoints[name]}px)`;
/** `(max-width: 767px) and (pointer: coarse)` — the phone-with-a-finger query. */
export const touchMediaQuery = (name = 'phone') => `${mediaQuery(name)} and (pointer: coarse)`;
/**
 * The band a width falls in. `null` means desktop (≥ 1280), where nothing responsive applies and
 * the geometry must stay byte-identical to the captured references.
 */
export function bandOf(width) {
    if (width <= breakpoints.phone)
        return 'phone';
    if (width <= breakpoints.tablet)
        return 'tablet';
    if (width <= breakpoints.laptop)
        return 'laptop';
    return null;
}
//# sourceMappingURL=breakpoints.js.map